import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import type { RoleDetail } from '@/lib/types/api'

const roleFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  slug: z
    .string()
    .min(2, 'Slug must be at least 2 characters')
    .max(100)
    .regex(
      /^[a-z0-9_-]+$/,
      'Slug must contain only lowercase letters, numbers, hyphens, and underscores'
    ),
  description: z.string().min(10, 'Description must be at least 10 characters').max(500),
})

type RoleFormValues = z.infer<typeof roleFormSchema>

interface RoleFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: RoleFormValues) => Promise<void>
  role?: RoleDetail | null
  mode: 'create' | 'edit'
}

export function RoleFormDialog({ open, onOpenChange, onSubmit, role, mode }: RoleFormDialogProps) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: {
      name: '',
      slug: '',
      description: '',
    },
  })

  // Update form when role changes
  useEffect(() => {
    if (role && mode === 'edit') {
      form.reset({
        name: role.name,
        slug: role.slug,
        description: role.description,
      })
    } else if (mode === 'create') {
      form.reset({
        name: '',
        slug: '',
        description: '',
      })
    }
  }, [role, mode, form])

  const handleSubmit = async (data: RoleFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
      onOpenChange(false)
    } catch (error) {
      // Error handling is done by the parent component
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    form.reset()
    onOpenChange(false)
  }

  const isSystemRole = role?.isSystem ?? false
  const isSlugDisabled = mode === 'edit' // Slug cannot be changed after creation

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? t('rbac.createRole') : t('rbac.editRole')}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? t('rbac.createRoleDescription') : t('rbac.editRoleDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('rbac.roleName')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('rbac.roleNamePlaceholder')}
                      disabled={isSystemRole}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('rbac.roleSlug')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('rbac.roleSlugPlaceholder')}
                      disabled={isSlugDisabled || isSystemRole}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('rbac.roleSlugDescription')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('rbac.roleDescription')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('rbac.roleDescriptionPlaceholder')}
                      className="resize-none"
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting || isSystemRole}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                {mode === 'create' ? t('rbac.createRole') : t('rbac.saveChanges')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
